import React, { useState, useEffect } from 'react';
import { Download, X, FileJson, AlertCircle } from 'lucide-react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { getStorageData } from '@/lib/storage';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';
import { ExportList } from './ExportList';
import { Button } from '@/components/ui/Button';

export function WorkspaceExportDialog({ className, children, externalOpen, setExternalOpen }: { className?: string, children?: React.ReactNode, externalOpen?: boolean, setExternalOpen?: (v: boolean) => void }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  
  const setIsOpen = (v: boolean) => {
    if (setExternalOpen) setExternalOpen(v);
    else setInternalOpen(v);
  };
  
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  const loadWorkspaces = async () => {
    setIsLoading(true);
    try {
      const data = await getStorageData<any[]>('resumeChecker_workspaces_v3', user?.uid);
      if (data) {
        setWorkspaces(data);
        setSelectedIds(new Set(data.map(w => w.id)));
      }
    } catch (err) {
      console.error('Failed to load workspaces for export', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadWorkspaces();
    }
  }, [isOpen]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.size === workspaces.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(workspaces.map(w => w.id)));
    }
  };

  const handleToggleWorkspace = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const handleExport = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one workspace to export.');
      return;
    }

    setIsExporting(true);
    try {
      const exportData: any = {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        workspaces: []
      };

      for (const w of workspaces) {
        if (selectedIds.has(w.id)) {
          const wData = await getStorageData(`workspace_${w.id}`, user?.uid);
          // Also fetch resumes? Maybe just everything associated if possible, but let's stick to the workspace data.
          exportData.workspaces.push({
            metadata: w,
            data: wData || {}
          });
        }
      }

      // Create blob and download
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resume-pro-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Workspaces exported successfully!');
      setIsOpen(false);
    } catch (err) {
      console.error('Export failed', err);
      toast.error('Failed to export data.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <button
        onClick={handleOpen}
        aria-label="Export Data"
        title="Export Workspaces Data"
        className={className || "flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer h-8 px-2 sm:px-3 rounded-md"}
      >
        {children || (
          <>
            <Download className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Export</span>
          </>
        )}
      </button>

      <ResponsiveOverlay
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        variant="modal"
        className="w-full sm:max-w-md max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-2">
            <div className="bg-blue-100 dark:bg-blue-500/20 p-1.5 rounded-md">
              <FileJson className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Export Data</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            aria-label="Close Export Dialog"
            className="rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="overflow-y-auto p-6 flex flex-col gap-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 p-3 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 dark:text-amber-400">
              <strong>Data Storage:</strong> Data is saved locally in your browser. Browsers may clear this if storage is low or history is cleared. Please routinely export your data to prevent loss and access it on other devices.
            </div>
          </div>

          <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
            Select the workspaces you want to export. The data will be downloaded as a JSON file.
          </p>

          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">Your Workspaces</h3>
            <button
              onClick={handleToggleSelectAll}
              className="text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer"
            >
              {selectedIds.size === workspaces.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="space-y-2 max-h-[250px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 pr-2">
            <ExportList 
              workspaces={workspaces}
              selectedIds={selectedIds}
              handleToggleWorkspace={handleToggleWorkspace}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 shrink-0 sm:rounded-b-2xl">
          <button
            onClick={() => setIsOpen(false)}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || selectedIds.size === 0}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-6 rounded-xl transition-colors text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? 'Exporting...' : 'Export Selected'}
          </button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}
